/**
 * Unified job polling — ONE polling loop for every async capability.
 *
 * The backend has exactly one job status endpoint, `GET /api/v1/jobs/{job_id}`,
 * backed by the UnifiedJobRegistry. It resolves jobs from every registered
 * store on both servers, and (on desktop) falls back to asking Cloud Run about
 * jobs this process proxied there.
 *
 * Before integrationMode Stage 01 there were fourteen per-capability status
 * routes in four URL shapes, and this directory mirrored them with a dozen
 * copies of the same `getStatus` + `waitForCompletion` pair. That is the
 * duplication RULE #-2 forbids: every copy was a separate status vocabulary,
 * a separate progress scale, and a separate 404 behaviour. Capability services
 * now delegate here.
 *
 * RULE #16: `apiClient` (auth + base URL) and `unwrapEnvelope` (response
 * envelope) are the single sources of truth for those two concerns — this
 * module adds the third one, "how do I poll a job", and nothing else.
 */

import { apiClient } from './apiClient';
import { unwrapEnvelope } from '../utils/httpUtils';

/** Canonical job statuses (mirrors CANONICAL_JOB_STATUSES in shared/protocols/job_store.py). */
export type JobStatusValue =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'awaiting_review';

/** Terminal statuses — a job in one of these can never change again. */
const TERMINAL_STATUSES: readonly string[] = ['completed', 'failed', 'cancelled'];

/**
 * The shape `GET /api/v1/jobs/{job_id}` returns.
 *
 * `progress` is 0-100, always — the backend's canonical scale. Capability
 * payload (audio_url, lyrics, provider, cost, …) arrives under
 * `extra_metadata`, which is where store-specific data belongs.
 */
export interface UnifiedJob {
  job_id: string;
  status: JobStatusValue;
  progress: number;
  message: string;
  result_url: string | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
  elapsed_seconds: number | null;
  estimated_remaining_seconds: number | null;
  estimated_total_seconds: number | null;
  current_stage: string | null;
  current_stage_label: string | null;
  current_step: number | null;
  total_steps: number | null;
  extra_metadata?: Record<string, unknown>;
}

/** True once the job can no longer change. */
export function isJobTerminal(status: string): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/** Fetch a job's current status from the unified registry. */
export async function getJobStatus<T = UnifiedJob>(jobId: string): Promise<T> {
  if (!jobId || jobId === 'undefined') {
    throw new Error('Cannot check status: invalid job ID');
  }
  const response = await apiClient.get(`/api/v1/jobs/${jobId}`);
  return unwrapEnvelope<T>(response.data);
}

/**
 * Re-read a finished job and return its `result_url` as the backend signs it now.
 *
 * A finished job's result link is often a presigned S3 URL that dies an hour
 * after it was signed, and the backend re-signs it on every read of the job. A
 * screen that holds a result outside GenerationContext calls this at the moment
 * of use -- a player that errored, a download -- instead of reusing the link it
 * stored. Rejects when the job is not completed or carries no link.
 */
export async function getFreshResultUrl(jobId: string): Promise<string> {
  const job = await getJobStatus(jobId);
  if (job.status !== 'completed' || !job.result_url) {
    throw new Error(`Job ${jobId} has no result link to renew (status: ${job.status})`);
  }
  return job.result_url;
}

/**
 * Poll a job until it reaches a terminal status.
 *
 * @param jobId - The id returned by the capability's submit endpoint.
 * @param onProgress - Called with every polled snapshot, including the last.
 * @param pollIntervalMs - Delay between polls.
 * @returns The terminal job snapshot. Callers inspect `status` — this does not
 *   throw on `failed`, because "the job failed" is an answer, not a transport
 *   error.
 */
export async function waitForJobCompletion<T extends { status: string }>(
  jobId: string,
  onProgress?: (job: T) => void,
  pollIntervalMs = 3000
): Promise<T> {
  for (;;) {
    const job = await getJobStatus<T>(jobId);

    onProgress?.(job);

    if (isJobTerminal(job.status)) {
      return job;
    }

    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }
}
